import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { motion } from 'framer-motion';
import {
  Flex,
  Text,
  VStack,
  useMediaQuery,
  useTheme,
  Box,
} from '@chakra-ui/react';
import { MdArrowForward, MdPlayCircle } from 'react-icons/md';
import BlogExpertBadge from '@repo/ui-components/tailwind/BlogExpertBadge';
import Link from '@repo/ui-components/tailwind/Link';
import SanityBackgroundImage from '../SanityBackgroundImage';

function CardBlogStandard({ article, badge }) {
  const theme = useTheme();
  const [isLgScreen] = useMediaQuery(`(min-width: ${theme.breakpoints.lg})`);
  const [isHovered, setIsHovered] = useState(false);

  if (!article) return null;

  const categories = article?.categories || article?.subCategories;
  const isVideo = /video/i.test(article?.articleType);

  return (
    <VStack pos="relative" gap={0}>
      {badge?.headline &&
        article.author?.title &&
        article.author?.slug?.current && (
          <BlogExpertBadge
            className="z-50 absolute top-2 left-2"
            author={article.author?.title}
            slug={article.author?.slug?.current}
            badge={badge}
          />
        )}
      <Box
        as={Link}
        link={`/blog/${article.slug?.current}`}
        width="100%"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        w="100%"
      >
        <SanityBackgroundImage
          as={Flex}
          height={['377px', '474px', '578px', '474px']}
          borderRadius="24px"
          gradientGenerator={url =>
            `linear-gradient(0deg, rgba(0, 0, 0, 0.20) 0%, rgba(0, 0, 0, 0.20) 100%), linear-gradient(0deg, rgba(0, 0, 0, 0.60) 0%, rgba(0, 0, 0, 0.40) 60%, rgba(0, 0, 0, 0.00) 100%), url(${url}), lightgray 50% / cover no-repeat`
          }
          image={article?.image}
          maxImageWidth={900}
          p={8}
          transition="filter .1s ease-in-out"
          _hover={{ filter: 'brightness(.6)' }}
          flexDirection="column"
          justifyContent="space-between"
          alignItems="flex-end"
          overflow="hidden"
        >
          <motion.div
            initial={{ opacity: 0 }}
            animate={isLgScreen && isHovered ? { opacity: 1 } : { opacity: 0 }}
            transition={{ duration: 0.4 }}
          >
            <Flex
              background="brand.sunny"
              borderRadius={30}
              width={10}
              height={10}
              alignItems="center"
              justifyContent="center"
              id="icon"
            >
              <MdArrowForward size="28px" />
            </Flex>
          </motion.div>

          <Flex flexDirection="column" width="100%" alignItems="flex-start">
            <motion.div
              initial={{ y: 0 }}
              animate={isLgScreen && isHovered ? { y: 0 } : { y: 24 }}
              transition={{ duration: 0.4 }}
            >
              <Flex mb={1} flexDirection="row" flexWrap="wrap">
                {categories?.map((category, index) => (
                  <Text
                    key={category._id || category._key}
                    textStyle="bodyLarge"
                    fontWeight="bold"
                    color="brand.sunny"
                    mr={2}
                  >
                    {category.title}
                    {index !== categories.length - 1 && ' • '}
                  </Text>
                ))}

                {isVideo && (
                  <Flex alignItems="center" gap={1}>
                    <Text
                      textStyle="bodyLarge"
                      fontWeight="bold"
                      color="brand.sunny"
                    >
                      {article.categories?.length && ' • '}
                      {article.articleType}
                    </Text>
                    <Text color="brand.sunny">
                      <MdPlayCircle size={20} />
                    </Text>
                  </Flex>
                )}
              </Flex>

              <Text
                as="h3"
                textStyle="h3"
                fontWeight="black"
                color="white"
                mb={[6, null, 2]}
              >
                {article.title}
              </Text>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 0, height: 0 }}
              animate={
                isLgScreen && isHovered
                  ? { opacity: 1, y: 0, height: 'max-content' }
                  : { opacity: 0, y: 24, height: 0 }
              }
              transition={{ duration: 0.4 }}
            >
              <Text
                fontWeight="bold"
                color="white"
                noOfLines={5}
                dangerouslySetInnerHTML={{ __html: article.description }}
              />
            </motion.div>
          </Flex>
        </SanityBackgroundImage>
      </Box>
    </VStack>
  );
}

CardBlogStandard.propTypes = {
  article: PropTypes.shape({
    _key: PropTypes.string,
    _id: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    articleType: PropTypes.string,
    slug: PropTypes.shape({
      current: PropTypes.string.isRequired,
    }).isRequired,
    image: PropTypes.shape({
      asset: PropTypes.shape({
        url: PropTypes.string.isRequired,
      }).isRequired,
      alt: PropTypes.string,
    }).isRequired,
    description: PropTypes.string,
    subCategories: PropTypes.arrayOf(
      PropTypes.shape({
        _key: PropTypes.string,
        _type: PropTypes.string,
        title: PropTypes.string,
        slug: PropTypes.shape({ current: PropTypes.string.isRequired }),
      })
    ),
    categories: PropTypes.arrayOf(
      PropTypes.shape({
        _key: PropTypes.string,
        _type: PropTypes.string,
        title: PropTypes.string,
        slug: PropTypes.shape({ current: PropTypes.string.isRequired }),
      })
    ),
    author: PropTypes.shape({
      title: PropTypes.string.isRequired,
      slug: PropTypes.shape({
        current: PropTypes.string.isRequired,
      }).isRequired,
    }),
  }).isRequired,
  badge: PropTypes.shape({
    headline: PropTypes.string,
    copy: PropTypes.string,
    link: PropTypes.shape({
      linkType: PropTypes.string,
      href: PropTypes.string,
      internalLink: PropTypes.shape({
        slug: PropTypes.shape({
          current: PropTypes.string,
        }),
      }),
    }),
    icon: PropTypes.shape({
      asset: PropTypes.shape({
        url: PropTypes.string,
      }),
    }),
  }),
};

CardBlogStandard.defaultProps = {
  badge: null,
};

export default CardBlogStandard;
